import { useEffect, useState, useRef } from "react";
import { Loader2 } from "lucide-react";
import { empresaNotificacionesService } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import Swal from "sweetalert2";
import ContactosSmsConfig from "../notificaciones/ContactosSmsConfig";

// ─── Metadatos locales para tipos conocidos ───────────────────────────────────
const CARD_META = {
  PARADA_PROLONGADA: {
    icon: "⏱️",
    iconBg: "bg-orange-50",
    desc: "El vehículo lleva más de 10 minutos consecutivos detenido durante el viaje.",
  },
  PARADA_NO_PROGRAMADA: {
    icon: "🖐️",
    iconBg: "bg-orange-50",
    desc: "El vehículo se detiene fuera de los puntos planificados o zonas geocercadas autorizadas.",
  },
  EXCESO_VELOCIDAD: {
    icon: "⚠️",
    iconBg: "bg-yellow-50",
    desc: "El vehículo supera el límite de velocidad establecido para la flota.",
    speedField: true,
  },
  RALENTI_PROLONGADO: {
    icon: "🔴",
    iconBg: "bg-red-50",
    desc: "Motor encendido y vehículo detenido por más de 15 minutos consecutivos.",
  },
  PERDIDA_SENAL_GPS: {
    icon: "📡",
    iconBg: "bg-green-50",
    desc: "El dispositivo telemático deja de reportar ubicación por más de 15 minutos.",
  },
  BATERIA_DISPOSITIVO_BAJA: {
    icon: "🔋",
    iconBg: "bg-gray-50",
    desc: "La batería del dispositivo GPS cae por debajo del umbral mínimo de operación.",
  },
  LLEGADA_TARDE: {
    icon: "🕐",
    iconBg: "bg-yellow-50",
    desc: "El vehículo llega fuera del horario programado a un punto del viaje.",
  },
  DESVIO_RUTA: {
    icon: "🔀",
    iconBg: "bg-purple-50",
    desc: "Alejamiento significativo de la ruta trazada y asignada originalmente al vehículo.",
  },
  VIAJE_CREADO: {
    icon: "🚛",
    iconBg: "bg-blue-50",
    desc: "Se notifica cuando se registra un nuevo viaje en el sistema.",
  },
  LLEGADA_CONFIRMADA_ORIGEN: {
    icon: "📍",
    iconBg: "bg-green-50",
    desc: "El vehículo confirmó llegada al punto de origen.",
  },
  LLEGADA_CONFIRMADA_DESTINO: {
    icon: "🏁",
    iconBg: "bg-green-50",
    desc: "El vehículo confirmó llegada al destino final.",
  },
  PRE_AVISO_LLEGADA_ORIGEN: {
    icon: "🔔",
    iconBg: "bg-blue-50",
    desc: "Aviso anticipado de llegada al punto de origen.",
  },
  PRE_AVISO_LLEGADA_DESTINO: {
    icon: "🔔",
    iconBg: "bg-blue-50",
    desc: "Aviso anticipado de llegada al destino final.",
  },
};

const DEFAULT_META = { icon: "📋", iconBg: "bg-gray-50", desc: "" };

// ─── Toggle ───────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
        checked ? "bg-green-500" : "bg-gray-300"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
      {checked && (
        <span className="absolute right-1 top-1/2 -translate-y-1/2 text-white text-[10px] font-bold pointer-events-none">
          ✓
        </span>
      )}
    </button>
  );
}

// ─── Tarjeta individual ───────────────────────────────────────────────────────
function AlertCard({ tipo, label, active, onToggle, saving, speedLimit, onSpeedChange }) {
  const meta = CARD_META[tipo] ?? DEFAULT_META;

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 flex flex-col gap-3 transition-shadow duration-200 hover:shadow-md">
      {/* Fila superior: ícono + toggle */}
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl shrink-0 ${meta.iconBg}`}>
          {meta.icon}
        </div>
        <div className="shrink-0">
          {saving ? (
            <Loader2 size={20} className="animate-spin text-blue-400" />
          ) : (
            <Toggle checked={active} onChange={onToggle} />
          )}
        </div>
      </div>

      {/* Título y descripción: ancho completo sin competir con nada */}
      <div className="flex flex-col gap-1">
        <h3 className="text-[15px] font-bold text-gray-900 leading-snug">{label}</h3>
        {meta.desc && (
          <p className="text-sm text-gray-500 leading-relaxed">{meta.desc}</p>
        )}
      </div>

      {meta.speedField && (
        <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5">
          <span className="text-sm font-medium text-gray-700 flex-1">Límite Global:</span>
          <input
            type="number"
            min={0}
            max={200}
            value={speedLimit ?? 90}
            onChange={(e) => onSpeedChange?.(Number(e.target.value))}
            className="w-16 text-center border border-gray-300 rounded-md px-2 py-1 text-sm font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <span className="text-xs text-gray-400 font-medium">km/h</span>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function ConfigNotificacionesAlertas() {
  const { user } = useAuth();
  const [tipos, setTipos] = useState([]);
  const [config, setConfig] = useState({});
  const [speedLimit, setSpeedLimit] = useState(90);
  const [contactosSms, setContactosSms] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const speedDebounce = useRef(null);

  const empresaId = user?.empresa_id ?? "748c2bbd-9837-11f0-9137-dc4a3e99c801";

  useEffect(() => {
    if (!empresaId) return;
    const cargar = async () => {
      setLoading(true);
      try {
        const res = await empresaNotificacionesService.getConfig(empresaId);
        const data = res.data ?? {};
        setTipos(data.tipos ?? []);
        const estadoInicial = {};
        (data.tipos ?? []).forEach(({ tipo, enabled }) => {
          estadoInicial[tipo] = enabled;
        });
        setConfig(estadoInicial);
        if (data.velocidad_limite) setSpeedLimit(data.velocidad_limite);
        setContactosSms(data.contactos_sms ?? {});
      } catch {
        setTipos([]);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [empresaId]);

  const handleToggle = async (tipo) => {
    if (!empresaId) return;
    const nuevo = { ...config, [tipo]: !config[tipo] };
    setConfig(nuevo);
    setSaving(tipo);
    try {
      await empresaNotificacionesService.updateConfig(empresaId, nuevo, speedLimit);
    } catch {
      setConfig(config);
      Swal.fire({ icon: "error", title: "Error al guardar", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
    } finally {
      setSaving(null);
    }
  };

  const handleSpeedChange = (valor) => {
    if (!empresaId) return;
    setSpeedLimit(valor);
    clearTimeout(speedDebounce.current);
    speedDebounce.current = setTimeout(async () => {
      try {
        await empresaNotificacionesService.updateConfig(empresaId, config, valor);
      } catch {
        Swal.fire({ icon: "error", title: "Error al guardar límite", toast: true, position: "top-end", showConfirmButton: false, timer: 3000 });
      }
    }, 800);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-7">
        <p className="text-xs text-gray-400 mb-1">
          Viajes &rsaquo; <span className="text-blue-500">Configuración de Alertas</span>
        </p>
        <h2 className="text-xl font-bold text-gray-900">Configuración de Notificaciones</h2>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 size={32} className="animate-spin text-blue-400" />
        </div>
      ) : tipos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <p className="text-sm">No hay reglas de notificación activas configuradas.</p>
          <p className="text-xs mt-1">El SuperAdmin debe crear reglas en Gestión de Reglas.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {tipos.map(({ tipo, label }) => (
              <AlertCard
                key={tipo}
                tipo={tipo}
                label={label}
                active={config[tipo] ?? true}
                onToggle={() => handleToggle(tipo)}
                saving={saving === tipo}
                speedLimit={speedLimit}
                onSpeedChange={(CARD_META[tipo]?.speedField) ? handleSpeedChange : undefined}
              />
            ))}
          </div>

          <ContactosSmsConfig
            empresaId={empresaId}
            contactosIniciales={contactosSms}
          />
        </>
      )}
    </div>
  );
}
